import type { RouteDef } from '@/app/routes'
import type { WrittenPage } from '../sections'
import { LINKS } from './links'

/**
 * Become a Digitopian — the Careers page the header and footer both point at
 * (`LINKS.careers`).
 *
 * It was an **imported** WordPress page until 2026-09-07: a heading and four
 * paragraphs, with no way to list a role
 * (`content/imported/pages/en/become-a-digitopian.json`, still on disk as the
 * record of what it said). That morning it became a written page — the
 * imported words above a `cards` band, one card per open role — and by the
 * afternoon Kagan had asked for it to be rebuilt properly (*"biz baştan aşağıya
 * kuralım orayı"*, then *"şu careers sayfasını da düzeltir misin"*). What was
 * wrong with the morning version, so it does not come back:
 *
 *   * the hero's only button said "Get in touch" and jumped to the **sales
 *     contact form** at the foot of the page — on a page whose whole point is
 *     five roles one screen further down;
 *   * the imported prose sat as one thin sentence on a grey band between the
 *     hero and the roles, a section with nothing in it;
 *   * the page ended in the shared contact band: Business Email, Company Name,
 *     Industry — a form for buyers, under a list of jobs.
 *
 * So the page now reads the way a candidate reads it: the roles first (the
 * hero's button anchors to them — `anchor: 'roles'` on the band), then what it
 * is like to work here, then how hiring works, then a way in for the person
 * whose role is not listed. The contact band is suppressed for this slug in
 * `app/Layout.tsx`; the closing `cta` sends that person to Contact Us.
 *
 * Nothing in "What it's like here" is invented for this page: each item is
 * taken from the offer the role pages already make in their own "What's
 * in it for you" rows (hybrid Istanbul, flexible hours, generous leave, an
 * agentic development team, mentorship, stock options), so the listing page and
 * the role pages promise the same things. "How we hire" is the pipeline the
 * application actually moves through in Excellence (`hr.application_stages`:
 * applied → screening → interview → offer), written for the candidate — and
 * the acknowledgement email it mentions is the one `website-job-application`
 * really sends.
 *
 * Cards: six fall as two clean rows of three (five fell as three and two
 * until the Forward Deployed AI Engineer landed on 2026-09-10). They ended in
 * pill buttons for a morning (*"preview kısmı view role diye butonlar ile
 * dolu"*), so they are the text CTA ("View role →") with the title linked as
 * well — one way in per card, said quietly.
 *
 * The slug is unchanged, so the header, the footer and any inbound link to
 * digitopia.co's careers page still land here; `SUPERSEDED_BY_WRITTEN` in
 * `src/legacy.ts` is what stops the imported version overwriting this one's
 * HTML.
 */
export const route: RouteDef = {
  id: 'becomeADigitopian',
  path: 'become-a-digitopian',
  priority: 0.7,
}

export const becomeADigitopian: WrittenPage = {
  navLabel: 'Careers',
  title: 'Become a Digitopian',
  description:
    'Join Digitopia and shape the future of transformation. Six open roles in Istanbul: AI Engineer, Product Engineer, Forward Deployed AI Engineer, Transformation Manager, Growth Engineer and Strategy & Management Consultant.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Careers',
      headline: 'Join us and shape the future of transformation.',
      sub: 'Six open roles. One team that is reinventing how consulting works.',
      body: [
        'Digitopia helps ambitious organizations understand where they stand, decide what matters next and turn their digital and AI ambitions into measurable progress. We build the Impact Platform and the maturity frameworks behind it, and we work alongside the leadership teams who use them.',
        'If you like hard problems, real ownership and a team that ships, one of the roles below may be yours.',
      ],
      ctas: [
        { label: 'See open roles', href: '#roles' },
        { label: 'Our values', href: '/en/about/our-values' },
      ],
    },
    {
      kind: 'cards',
      anchor: 'roles',
      heading: 'Open roles',
      sub: 'All six are hybrid in Istanbul and full-time. Open the one that sounds like you.',
      linkTitles: true,
      items: [
        {
          kicker: 'Hybrid (Istanbul) · Full-time',
          title: 'AI Engineer',
          promise:
            'Turn fast-moving AI technology into dependable products: agents, evals, guardrails, retrieval and the platform services behind them.',
          points: [],
          cta: { label: 'View role', href: '/en/become-a-digitopian/ai-engineer' },
        },
        {
          kicker: 'Hybrid (Istanbul) · Full-time',
          title: 'Forward Deployed AI Engineer',
          promise:
            'A hands-on builder, curious consultant and structured problem solver in one: take an ambiguous client problem from the first conversation to a production AI solution.',
          points: [],
          cta: {
            label: 'View role',
            href: '/en/become-a-digitopian/forward-deployed-ai-engineer',
          },
        },
        {
          kicker: 'Hybrid (Istanbul) · Full-time',
          title: 'Product Engineer',
          promise:
            'A full-stack builder with strong product judgment: own features end to end and turn complex transformation workflows into simple, dependable experiences.',
          points: [],
          cta: { label: 'View role', href: '/en/become-a-digitopian/product-engineer' },
        },
        {
          kicker: 'Hybrid (Istanbul) · Full-time',
          title: 'Transformation Manager',
          promise:
            'Turn strategic roadmaps into a mobilized portfolio with clear ownership, disciplined governance and visible business value.',
          points: [],
          cta: { label: 'View role', href: '/en/become-a-digitopian/transformation-manager' },
        },
        {
          kicker: 'Hybrid (Istanbul) · Full-time',
          title: 'Growth Engineer',
          promise:
            'Part builder, part analyst, part commercial problem solver: build repeatable systems for qualified demand, conversion, expansion and learning.',
          points: [],
          cta: { label: 'View role', href: '/en/become-a-digitopian/growth-engineer' },
        },
        {
          kicker: 'Hybrid (Istanbul) · Full-time',
          title: 'Strategy & Management Consultant',
          promise:
            'Take leadership teams from a fact-based baseline to strategy, and from strategy to an executable roadmap with measurable value.',
          points: [],
          cta: {
            label: 'View role',
            href: '/en/become-a-digitopian/strategy-management-consultant',
          },
        },
      ],
    },
    {
      kind: 'features',
      heading: 'What it’s like here',
      sub: 'A small team with a large remit. The same offer, in every role.',
      columns: 3,
      items: [
        {
          title: 'Real ownership',
          body: 'You own a problem from exploration to production. Decisions are made close to the work, and the person who made them is expected to see them through.',
        },
        {
          title: 'AI-native ways of working',
          body: 'Our development team already works 100% agentically. Whatever your role, you will use the newest models, agents and tools every day — and help decide how we use them.',
        },
        {
          title: 'Work that lands',
          body: 'What you build reaches leadership teams at large enterprises and changes how they run transformation. You see the effect of your work, not a slide about it.',
        },
        {
          title: 'Hybrid in Istanbul, flexible hours',
          body: 'A base in Istanbul, a schedule that respects your life, and generous paid time off. We measure output, not presence.',
        },
        {
          title: 'People who share what they know',
          body: 'Mentorship, courses, experimentation time and colleagues who value authenticity, curiosity and bold ideas. Share to enable is one of our values because we mean it.',
        },
        {
          title: 'Rewards that last',
          body: 'Competitive compensation with stock options tied to your lasting impact — because a Digitopian is an owner, not a headcount.',
        },
      ],
    },
    {
      kind: 'list',
      heading: 'How we hire',
      intro:
        'Four steps, no maze. We read every application ourselves and we tell you where you stand at each one.',
      variant: 'steps',
      items: [
        'Apply on the role page — your CV and a few lines on why this role. You get a confirmation email within a minute.',
        'A real read, by real people. Every application is read by the hiring manager and the team you would join, and you hear back from us either way.',
        'Conversations with the team — about the work, how you think and what you would do here first. Expect real problems, not puzzles.',
        'An offer, with the details in writing, and a start date that works for you.',
      ],
      body: 'Your application and CV are kept for twelve months so we can come back to you when a role fits, unless you ask us to delete them sooner.',
    },
    {
      kind: 'cta',
      tone: 'band',
      heading: 'Don’t see your role yet?',
      sub: 'Tell us what you do best and why Digitopia. When a role opens that fits, you will hear from us first.',
      headingHref: LINKS.contact,
      ctas: [{ label: 'Get in touch', href: LINKS.contact }],
    },
  ],
}
